import React, {useEffect, useRef, useState} from 'react'
import {Button, Checkbox, CircularProgress, FormControlLabel, TextField, Tooltip, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {toast} from 'react-toastify'
import {useDispatch} from 'react-redux'
import api from '../../../api/axios'
import {setSignUpStep, signOut} from '../../../store/slices/authSlice'
import {useNavigate} from 'react-router-dom'
import StageExitAction from './StageExitAction'

const CODE_LENGTH = 6
const RESEND_SECONDS = 30
const CONFIRM_COOLDOWN_SECONDS = 5

const SignupEmailVerificationStep: React.FC = () => {
  const {t} = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''))
  const [cooldown, setCooldown] = useState(0)
  const [confirmCooldown, setConfirmCooldown] = useState(0)
  const [loadingRequestCode, setLoadingRequestCode] = useState(false)
  const [loadingConfirm, setLoadingConfirm] = useState(false)
  const [loadingExit, setLoadingExit] = useState(false)
  const [codeRequested, setCodeRequested] = useState(false)
  const [newsletter, setNewsletter] = useState(true)

  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  // Validate email format
  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    setEmailError(emailRegex.test(value) ? '' : t('contact.invalid_email'))
  }

  // Handle resend cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown(prev => prev - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  // Handle confirm button cooldown timer
  useEffect(() => {
    if (confirmCooldown <= 0) return
    const timer = setTimeout(() => setConfirmCooldown(prev => prev - 1), 1000)
    return () => clearTimeout(timer)
  }, [confirmCooldown])

  // Request verification code via API
  const requestCode = async () => {
    if (!email.trim() || emailError || cooldown > 0) return
    try {
      setLoadingRequestCode(true)
      await api.post('/user/request-email-verification', {email, changeRequest: false})
      toast.success(t('signup.email.codeSent'))
      setCooldown(RESEND_SECONDS)
      setCodeRequested(true)
      setCode(Array(CODE_LENGTH).fill(''))
      inputRefs.current[0]?.focus()
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('signup.email.codeError'))
    } finally {
      setLoadingRequestCode(false)
    }
  }

  // Submit verification code to proceed
  const continueSignup = async () => {
    if (!codeRequested || confirmCooldown > 0) return
    const fullCode = code.join('')
    if (fullCode.length !== CODE_LENGTH) return
    try {
      setLoadingConfirm(true)
      setConfirmCooldown(CONFIRM_COOLDOWN_SECONDS)
      await api.post('/user/confirm-email-sign-up', {
        email,
        code: fullCode,
        changeRequest: false,
        newsletter
      })
      toast.success(t('signup.email.success'))
      dispatch(setSignUpStep({signUpStep: 'ADD_TRADE_URL'} as any))
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('signup.email.invalidCode'))
    } finally {
      setLoadingConfirm(false)
    }
  }

  // Handle single digit input and auto focus next
  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return
    const next = [...code]
    next[index] = value
    setCode(next)
    if (value && index < CODE_LENGTH - 1) inputRefs.current[index + 1]?.focus()
  }

  // Handle backspace navigation
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) inputRefs.current[index - 1]?.focus()
  }

  // Handle code paste functionality
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH)
    if (!pasted) return
    const next = pasted.split('')
    while (next.length < CODE_LENGTH) next.push('')
    setCode(next)
    inputRefs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus()
  }

  const codeComplete = code.every(d => d.length === 1)

  // Handle sign out and reset flow
  const handleExit = async () => {
    try {
      setLoadingExit(true)
      await api.post('/sign-out')
      dispatch(signOut())
      navigate('/')
    } catch (err) {
      toast.error(t('signOut.error'))
    } finally {
      setLoadingExit(false)
    }
  }

  return (
      <div className="relative mt-6">
        <Typography variant="body1">{t('signup.email.description')}</Typography>
        <div className="mb-1">
          <FormControlLabel
              control={
                <Checkbox
                    checked={newsletter}
                    onChange={e => setNewsletter(e.target.checked)}
                    color="primary"
                />
              }
              label={t('signup.email.newsletter_label') || 'Subscribe to our newsletter'}
          />
        </div>
        <div className="flex gap-2 items-start mt-6">
          <TextField
              label={t('signup.email.label')}
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setCodeRequested(false)
              }}
              onBlur={() => {
                setEmailTouched(true);
                validateEmail(email)
              }}
              error={emailTouched && !!emailError}
              helperText={emailTouched && emailError ? emailError : ' '}
              fullWidth
              type="email"
          />

          <Button
              variant="contained"
              onClick={requestCode}
              disabled={loadingRequestCode || cooldown > 0 || !!emailError || !email.trim()}
              className="h-[56px] w-60 relative"
          >
            <span className={loadingRequestCode ? 'invisible' : ''}>
              {t('signup.email.requestCode')}
            </span>
            {loadingRequestCode && (
                <CircularProgress size={24} color="inherit" className="absolute left-1/2 top-1/2 -ml-[12px] -mt-[12px]" />
            )}
          </Button>
        </div>

        <div className="min-h-[20px]">
          {cooldown > 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('signup.email.resendInfo', {seconds: cooldown})}
              </Typography>
          )}
        </div>

        <div className="flex justify-center gap-2 mt-6 mb-8">
          {code.map((digit, index) => (
              <TextField
                  key={index}
                  inputRef={el => inputRefs.current[index] = el}
                  value={digit}
                  onChange={e => handleCodeChange(index, e.target.value)}
                  inputProps={{
                    maxLength: 1,
                    className: 'text-center text-xl',
                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e),
                    onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e)
                  }}
                  sx={{width: 48}}
              />
          ))}
        </div>

        <div className="flex justify-between mt-6">
          <StageExitAction onExit={handleExit} loading={loadingExit}/>
          <div>
            <Tooltip
                title={!codeRequested
                    ? t('signup.email.requestFirst')
                    : confirmCooldown > 0
                        ? t('signup.email.confirmCooldown', {seconds: confirmCooldown})
                        : ''}
                disableHoverListener={codeRequested && confirmCooldown === 0}
            >
          <span>
            <Button
                variant="contained"
                onClick={continueSignup}
                disabled={!codeRequested || !codeComplete || loadingConfirm || confirmCooldown > 0}
                className="min-w-[150px] relative"
            >
              <span className={loadingConfirm ? 'invisible' : ''}>
                {t('common.continue')}
              </span>
              {loadingConfirm && (
                  <CircularProgress size={24} color="inherit" className="absolute left-1/2 top-1/2 -ml-[12px] -mt-[12px]" />
              )}
            </Button>
          </span>
            </Tooltip>
          </div>
        </div>
      </div>
  )
}

export default SignupEmailVerificationStep